import type { MessageKey } from '../../i18n/messages.en.ts';

/**
 * MVP-272 Phase 1: this used to build the announced sentence itself (`Request ${title} by
 * ${artist}`). A pure module has no dictionary to translate that sentence with (the same reasoning
 * `requestErrors.ts`'s own doc comment gives for returning keys instead of importing one), so this
 * now returns the i18n key + params `GuestSongRow.tsx` — which already has `useT()` — resolves via
 * `t(key, params)`, rather than hardcoding the English composition here.
 *
 * Pure accessible-name selection for `GuestSongRow` (P2 fix, round 5) — split out so the exact key
 * a screen reader's announcement resolves from is testable under `node --test` without rendering
 * the `.tsx` button (this repo has no DOM test runner). The row's own visible content is two
 * `Highlighted` match fragments plus a bare duration string, which together read as nothing to
 * assistive tech — this states the actual action (`Request …`), not just the title, since a
 * screen-reader user hears this INSTEAD OF the visible text entirely.
 */
export interface GuestSongRowLabel {
  key: MessageKey;
  params: Record<string, string>;
}

/**
 * MVP-369: the announced name now ends with the catalogue code, as a CLAUSE rather than as two more
 * keys. `aria-label` on a button replaces every child, so the `#2631` MVP-274 put on the row was
 * visible to everyone except the one guest MVP-274 was written for.
 *
 * Why clauses and not four keys: the code is optional (`song.catalogId` is nullable), so folding it
 * into the request sentence means a with-code and a without-code variant of each of the two
 * existing keys — four sentences to keep in step across two dictionaries, where only one word
 * differs. A clause list keeps the request sentence as the one thing the dictionary owns and lets
 * the code be appended or not. `guest.songRow.catalogueCode` is deliberately lowercase and
 * fragmentary ("song number {code}") for the same reason: it is never a sentence on its own.
 */
export function guestSongRowAriaLabel(
  title: string,
  artist: string | null | undefined,
  catalogId?: string | number | null
): GuestSongRowLabel[] {
  const trimmedArtist = artist?.trim();
  const clauses: GuestSongRowLabel[] = [
    trimmedArtist
      ? { key: 'guest.songRow.requestWithArtist', params: { title, artist: trimmedArtist } }
      : { key: 'guest.songRow.requestTitleOnly', params: { title } },
  ];
  // Same `song.catalogId &&` condition the row renders the visible `#code` span under, so the
  // announced name and the printed one appear and disappear together rather than drifting apart.
  const code = catalogId === null || catalogId === undefined ? '' : String(catalogId).trim();
  if (code) clauses.push({ key: 'guest.songRow.catalogueCode', params: { code } });
  return clauses;
}
